import { ArrowForward, KeyboardArrowDown } from "@mui/icons-material";
import { Box, Divider, Grid, Link, MenuItem, Select, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";

import ActivityCard from "../components/ActivityCard";
import axios from "../utils/axios";
import Awards from "../components/Awards";
import VolunteeredHours from "../components/VolunteeredHours";
import { openSignInModal } from "../reducers/style";

const useStyles = makeStyles(() => ({
  link: {
    display: "flex",
    alignItems: "center",
    marginTop: 16,
    cursor: "pointer",
  },
  selected: {
    fontSize: 16,
    fontWeight: 700,
    color: (props) => (props.mode === "light" ? "#142667" : "#fff"),
  },
}));

export default function Feed() {
  const { mode, displaySignIn } = useSelector((state) => state.style);
  const {
    id,
    suggested_activity_ids: suggestedActivityIds,
    suggested_program_ids: suggestedProgramIds,
  } = useSelector((state) => state.profile);
  const { locations } = useSelector((state) => state.data);

  const dispatch = useDispatch();
  const classes = useStyles({ mode });

  const [loading, setLoading] = useState(true);
  const [suggestedActivities, setSuggestedActivities] = useState([]);
  const [suggestedProgrammes, setSuggestedProgrammes] = useState([]);
  const [activities, setActivities] = useState([]);
  const [links, setLinks] = useState({});
  const [region, setRegion] = useState("all");

  useEffect(() => {
    if (!id) {
      setActivities([]);
      setSuggestedActivities([]);
      setSuggestedProgrammes([]);
      setLoading(true);
    }
  }, [id]);

  // eslint-disable-next-line default-param-last
  const getActivityByIds = useCallback(async (url, ids = [], callback, linkKey) => {
    try {
      setLoading(true);
      const response = await axios.get(`${url}${ids.join("||")}`);
      callback((prev) => {
        const obj = {};
        prev.forEach((element) => {
          obj[element.id] = element;
        });
        (response.data.data || []).forEach((element) => {
          obj[element.id] = element;
        });
        return Object.values(obj);
      });
      setLinks((prev) => ({ ...prev, [linkKey]: response.data.links.next }));
      setLoading(false);
    } catch (error) {
      setLinks((prev) => ({ ...prev, [linkKey]: null }));
    }
  }, []);

  const handleScroll = useCallback(() => {
    const height = document.body.scrollHeight;
    const reached = window.pageYOffset + window.innerHeight + 300;
    if (reached > height) {
      const callbacks = [setActivities, setSuggestedActivities, setSuggestedProgrammes];
      [("future", "suggested", "programmes")].forEach((key, index) => {
        if (links[key]) {
          const callback = callbacks[index];
          getActivityByIds(links[key], [], callback, key);
        }
      });
    }
  }, [links, getActivityByIds]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Get Suggested Activities
  useEffect(() => {
    if (suggestedActivityIds?.length) {
      getActivityByIds("activities?filter[id]=", suggestedActivityIds, setSuggestedActivities, "suggested");
    }
  }, [suggestedActivityIds, getActivityByIds, id]);

  // Get Suggested Programmes
  useEffect(() => {
    if (suggestedProgramIds?.length) {
      getActivityByIds("programmes?include=activities,skills&filter[id]=", suggestedProgramIds, setSuggestedProgrammes, "programmes");
    }
  }, [suggestedProgramIds, getActivityByIds, id]);

  // Get All (paginated) future activities
  useEffect(() => {
    let url = `activities?filter[starts>]=${dayjs().toISOString()}`;
    if (region !== "all") {
      url += `&filter[location_id]=${region}`;
    }
    setActivities([]);
    getActivityByIds(url, [], setActivities, "future");
  }, [getActivityByIds, region, id]);

  useEffect(() => {
    if (displaySignIn && !id) {
      dispatch(openSignInModal());
    }
  }, [id, displaySignIn, dispatch]);

  useEffect(() => {
    if (links.future !== undefined && links.programmes !== undefined && links.suggested !== undefined) {
      setLoading(false);
    } else if (links.future !== undefined && displaySignIn) {
      setLinks((prev) => ({ ...prev, programmes: null, suggested: null }));
    }
  }, [links, id, displaySignIn]);

  const iconMemo = useCallback((props) => <KeyboardArrowDown {...props} />, []);

  const filterByMemo = useMemo(
    () => (
      <Box mt={id ? 2 : 0}>
        {id && <Divider />}
        <Box display="flex" flexDirection={{ xs: "column", sm: "row" }}>
          <Typography
            sx={{
              fontSize: [24, 26, 28],
              fontWeight: 800,
              lineHeight: ["44px", "44px", "64px"],
              letterSpacing: "0.6px",
              marginRight: 5,
            }}
          >
            Upcoming Activities
          </Typography>
          <Select
            variant="standard"
            value={region}
            label="Region"
            IconComponent={iconMemo}
            onChange={(event) => {
              setRegion(event.target.value);
            }}
            disableUnderline
            sx={{ color: "primary.main" }}
            classes={{ select: classes.selected }}
          >
            <MenuItem value="all">Choose a region</MenuItem>
            {locations.map((obj) => (
              <MenuItem key={obj.title} value={obj.id}>
                {obj.title}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>
    ),
    [region, classes.selected, locations, iconMemo, id]
  );

  const activitiesMemo = useMemo(() => {
    if (links.future === undefined || links.programmes === undefined || links.suggested === undefined) {
      return null;
    }
    const size = Math.max(suggestedActivities.length, suggestedProgrammes.length, activities.length);
    const all = [];
    const shouldPush = (activity) => region === "all" || activity.location_id === region;
    const returnedProgrammes = {};
    for (let index = 0; index < size; index += 1) {
      if (suggestedActivities[index]) {
        if (shouldPush(suggestedActivities[index])) {
          all.push({ ...suggestedActivities[index], navigateTo: `/activities/${suggestedActivities[index].id}` });
        }
      }
      if (suggestedProgrammes[index]) {
        if (shouldPush(suggestedProgrammes[index])) {
          all.push({ ...suggestedProgrammes[index], isProgramme: true, navigateTo: `/programmes/${suggestedProgrammes[index].id}` });
        }
      }
      if (activities[index]) {
        const isDouble = suggestedActivities.map((obj) => obj.id).includes(activities[index].id);
        const isInProgramme = suggestedProgrammes.map((obj) => obj.id).includes(activities[index].programme_id);
        if (!isDouble && !isInProgramme) {
          if (activities[index].programme_id) {
            returnedProgrammes[activities[index].programme_id] = [
              ...(returnedProgrammes[activities[index].programme_id] || []),
              activities[index],
            ];
          } else {
            all.push({ ...activities[index], navigateTo: `/activities/${activities[index].id}` });
          }
        }
      }
    }
    // Collate the programmes to one programme card.
    Object.keys(returnedProgrammes).forEach((programID) => {
      all.push({
        ...returnedProgrammes[programID][0],
        starts: undefined,
        isProgramme: true,
        navigateTo: `/programmes/${programID}`,
        activities: returnedProgrammes[programID],
      });
    });
    return all.map((obj) => (
      <Grid item xs={12} lg={6} key={`${obj.isProgramme ? "Programme" : "Activity"}-${obj.id}`}>
        <ActivityCard {...obj} />
      </Grid>
    ));
  }, [links, suggestedActivities, suggestedProgrammes, activities, region]);

  const LoadingMemo = useMemo(
    () =>
      [1, 2, 3, 4, 5, 6].map((key) => (
        <Grid item xs={12} lg={6} key={`Loading-${key}`}>
          <ActivityCard />
        </Grid>
      )),
    []
  );

  return (
    <Box>
      {id && (
        <Box>
          <Box display="flex" flexDirection={{ xs: "column", md: "row" }}>
            <VolunteeredHours />
            <Box mt={2} mr={2} />
            <Awards />
          </Box>
          <Box justifyContent="flex-end" display="flex">
            <Link href="/summary_of_service" className={classes.link}>
              View full service record <ArrowForward fontSize="small" />
            </Link>
          </Box>
        </Box>
      )}
      {filterByMemo}
      <Grid container spacing={4} my={4}>
        {activitiesMemo}
        {loading && LoadingMemo}
      </Grid>
    </Box>
  );
}
